import { useEffect, useState, type ReactNode } from 'react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';

type Bill = { id: string; billNumber: string; billDate: string; totalAmount: number; status: string };
type Rx = { id: string; prescriptionNumber: string; createdAt: string; doctor?: { name?: string } };
type Lab = { id: string; updatedAt: string; labTest?: { name?: string } };
type Discharge = {
  admissionId: string;
  ipdNumber: string | null;
  dischargeDate: string | null;
  finalDiagnosis?: string;
};

export default function PatientPortalRecords() {
  const [bills, setBills] = useState<Bill[]>([]);
  const [rx, setRx] = useState<Rx[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [discharges, setDischarges] = useState<Discharge[]>([]);
  const [appts, setAppts] = useState<Record<string, unknown>[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    patientPortal.getBills().then(setBills).catch(() => {});
    patientPortal.getPrescriptions().then(setRx).catch(() => {});
    patientPortal.getLabReports().then(setLabs).catch(() => {});
    patientPortal.getDischargeSummaries().then(setDischarges).catch(() => {});
    patientPortal.getAppointments().then(setAppts).catch(() => {});
  }, []);

  const dl = async (key: string, fn: () => Promise<void>) => {
    setDownloading(key);
    try {
      await fn();
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-8">
      <PatientPortalPageHero
        imageKey="records"
        eyebrow="Your data"
        title="Health records"
        subtitle="Download hospital PDFs for bills, prescriptions, lab reports, and discharge summaries"
        compact
      />

      <Section title="Appointments">
        {appts.length === 0 ? (
          <Empty />
        ) : (
          appts.map((a, i) => (
            <li key={i} className="p-3">
              {String(a.scheduledAt)} — {String((a.doctor as { name?: string })?.name ?? '')} (
              {String(a.status)})
            </li>
          ))
        )}
      </Section>

      <Section title="Bills">
        {bills.map((b) => (
          <Row key={b.id}>
            <span>
              {b.billNumber} — ₹{b.totalAmount} ({b.status})
            </span>
            <PdfBtn
              loading={downloading === b.id}
              onClick={() => dl(b.id, () => patientPortal.downloadBillPdf(b.id, b.billNumber))}
            />
          </Row>
        ))}
        {bills.length === 0 && <Empty />}
      </Section>

      <Section title="Prescriptions">
        {rx.map((r) => (
          <Row key={r.id}>
            <span>
              {r.prescriptionNumber} — {r.doctor?.name ?? ''}
            </span>
            <PdfBtn
              loading={downloading === r.id}
              onClick={() =>
                dl(r.id, () => patientPortal.downloadPrescriptionPdf(r.id, r.prescriptionNumber))
              }
            />
          </Row>
        ))}
        {rx.length === 0 && <Empty />}
      </Section>

      <Section title="Lab reports">
        {labs.map((l) => (
          <Row key={l.id}>
            <span>{l.labTest?.name ?? 'Lab test'}</span>
            <PdfBtn
              loading={downloading === l.id}
              onClick={() => dl(l.id, () => patientPortal.downloadLabPdf(l.id))}
            />
          </Row>
        ))}
        {labs.length === 0 && <Empty />}
      </Section>

      <Section title="Discharge summaries">
        {discharges.map((d) => (
          <Row key={d.admissionId}>
            <span>
              {d.ipdNumber ?? 'IPD'} — {d.finalDiagnosis ?? 'Discharge'}
            </span>
            <PdfBtn
              loading={downloading === d.admissionId}
              onClick={() =>
                dl(d.admissionId, () =>
                  patientPortal.downloadDischargePdf(d.admissionId, d.ipdNumber ?? undefined)
                )
              }
            />
          </Row>
        ))}
        {discharges.length === 0 && <Empty />}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="font-semibold mb-2">{title}</h2>
      <ul className="border rounded-lg bg-white divide-y text-sm">{children}</ul>
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <li className="p-3 flex justify-between items-center gap-2">{children}</li>;
}

function PdfBtn({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button type="button" className="text-teal-700 text-sm font-medium" disabled={loading} onClick={onClick}>
      {loading ? '…' : 'PDF'}
    </button>
  );
}

function Empty() {
  return <li className="p-4 text-muted-foreground">No records</li>;
}
